import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChampDuProfil, FeuilleDeProfil } from '@nextilt/commun';
import { api, EchecApi } from '../partage/api/client';
import type { ChangementDeLigneDeProfil, LigneDeProfil, ReponseProfil } from '../partage/api/types';
import { cles } from '../partage/relectures';
import { profilPasEcrit } from '../partage/vocabulaire';
import {
  avecLaPropositionGardee,
  avecLaPropositionRetiree,
  changementDeLigne,
  ligneEcrite,
  profilCorrige,
  profilDuFormulaire,
} from './reglesDuProfil';

/**
 * La lecture du profil, la même pour la page, le premier formulaire, l'accueil et la barre de
 * gauche. Elle repart au serveur à chaque ouverture de la page (`staleTime: 0`) : les contacts sont
 * relus dans les bulles, et une fiche corrigée entre-temps se voit ici.
 */
const LECTURE_DU_PROFIL = { queryKey: cles.profil, queryFn: api.profil, staleTime: 0 } as const;

/**
 * Le premier formulaire fermé (« Plus tard » ou « Enregistrer ») pendant cette visite : il ne
 * s'ouvre plus seul, même si une lecture partie avant que le serveur l'ait noté vu revient encore
 * avec `formulaireAMontrer` (ticket 03).
 */
let formulaireFermeCetteVisite = false;
export function formulaireDejaFerme(): boolean {
  return formulaireFermeCetteVisite;
}

/** Le prénom écrit, pour « Toi » sur l'accueil et l'e-mail dans la barre de gauche ; pas encore lu ou vide, null. */
export function usePrenom(): string | null {
  return useQuery(LECTURE_DU_PROFIL).data?.profil.prenom ?? null;
}

/**
 * Le profil du compte et ses gestes (chantier profil, ticket 02), sur le modèle des papiers
 * (`papiers/usePapiers.ts`) : chaque geste se voit tout de suite, la réponse du serveur est posée
 * dans la lecture, et un refus remet ce qu'il y avait et le dit. Rien d'autre n'est relu : le
 * profil ne change ni l'arbre, ni le jour.
 */
export function useProfil() {
  const client = useQueryClient();
  const cle = cles.profil;
  const lecture = useQuery(LECTURE_DU_PROFIL);
  const [annonce, setAnnonce] = useState<string | null>(null);

  const dire = useCallback((erreur: unknown) => {
    setAnnonce(profilPasEcrit(erreur instanceof EchecApi ? erreur.code : 'erreur'));
  }, []);

  const lu = useCallback(() => client.getQueryData<ReponseProfil>(cle), [client, cle]);
  const changer = useCallback(
    async (changement: (avant: ReponseProfil) => ReponseProfil) => {
      await client.cancelQueries({ queryKey: cle });
      client.setQueryData<ReponseProfil>(cle, (avant) => (avant ? changement(avant) : avant));
    },
    [client, cle]
  );

  /** Corriger un champ : le profil entier part ; refusé, le champ reprend ce qu'il avait. */
  const corrigerUnChamp = useCallback(
    (champ: ChampDuProfil, ecrit: string) => {
      const avant = lu();
      if (!avant) return;
      const corrige = profilCorrige(avant.profil, champ, ecrit);
      if (!corrige) return;
      const ancien = avant.profil[champ];
      void changer((l) => ({ ...l, profil: { ...l.profil, [champ]: corrige[champ] } }));
      api.ecrireLeProfil(corrige).then(
        ({ profil }) => {
          setAnnonce(null);
          void changer((l) => ({ ...l, profil: { ...l.profil, [champ]: profil[champ] } }));
        },
        (erreur: unknown) => {
          void changer((l) => ({ ...l, profil: { ...l.profil, [champ]: ancien } }));
          dire(erreur);
        }
      );
    },
    [lu, changer, dire]
  );

  /** Ajouter une ligne libre écrite : elle va à la fin de sa feuille, quand le serveur l'a. */
  const ajouterUneLigne = useCallback(
    async (feuille: FeuilleDeProfil, intitule: string, valeur: string): Promise<void> => {
      const ecrite = ligneEcrite(intitule, valeur);
      if (!ecrite) return;
      try {
        const { ligne } = await api.ajouterUneLigneDeProfil({ feuille, ...ecrite });
        setAnnonce(null);
        await changer((l) => ({ ...l, lignes: { ...l.lignes, [feuille]: [...l.lignes[feuille], ligne] } }));
      } catch (erreur) {
        dire(erreur);
      }
    },
    [changer, dire]
  );

  const avecLaLigne = (l: ReponseProfil, ligne: Pick<LigneDeProfil, 'id' | 'feuille'>, changement: ChangementDeLigneDeProfil) => ({
    ...l,
    lignes: {
      ...l.lignes,
      [ligne.feuille]: l.lignes[ligne.feuille].map((x) => (x.id === ligne.id ? { ...x, ...changement } : x)),
    },
  });

  /** Corriger l'intitulé ou la valeur d'une ligne : tout de suite ; refusé, elle reprend ce qu'elle avait. */
  const corrigerUneLigne = useCallback(
    (ligne: LigneDeProfil, partie: 'intitule' | 'valeur', ecrit: string) => {
      const changement = changementDeLigne(ligne, partie, ecrit);
      if (!changement) return;
      void changer((l) => avecLaLigne(l, ligne, changement));
      api.modifierUneLigneDeProfil(ligne.id, changement).then(
        ({ ligne: rendue }) => {
          setAnnonce(null);
          void changer((l) => avecLaLigne(l, ligne, { [partie]: rendue[partie] }));
        },
        (erreur: unknown) => {
          void changer((l) => avecLaLigne(l, ligne, { [partie]: ligne[partie] }));
          dire(erreur);
        }
      );
    },
    [changer, dire]
  );

  /** Retirer une ligne : elle part de l'écran tout de suite ; refusé, le profil est relu. */
  const retirerUneLigne = useCallback(
    (ligne: LigneDeProfil) => {
      void changer((l) => ({
        ...l,
        lignes: { ...l.lignes, [ligne.feuille]: l.lignes[ligne.feuille].filter((x) => x.id !== ligne.id) },
      }));
      api.retirerUneLigneDeProfil(ligne.id).then(
        () => setAnnonce(null),
        (erreur: unknown) => {
          void client.invalidateQueries({ queryKey: cle });
          dire(erreur);
        }
      );
    },
    [changer, client, cle, dire]
  );

  /**
   * Garder une proposition de Claude (chantier profil-pour-claude, ticket 02) : elle passe tout de
   * suite dans sa feuille, la pastille baisse ; la ligne du serveur la remplace. Refusée (déjà
   * gardée ou retirée ailleurs, sans réseau), le profil est relu et le refus dit.
   */
  const garderUneProposition = useCallback(
    (proposition: LigneDeProfil) => {
      void changer((l) => avecLaPropositionGardee(l, { ...proposition, proposee: false }));
      api.garderUneProposition(proposition.id).then(
        ({ ligne }) => {
          setAnnonce(null);
          void changer((l) => avecLaPropositionGardee(l, ligne));
        },
        (erreur: unknown) => {
          void client.invalidateQueries({ queryKey: cle });
          dire(erreur);
        }
      );
    },
    [changer, client, cle, dire]
  );

  /** Non : la proposition part tout de suite (le retrait d'une ligne) ; refusé, le profil est relu. */
  const refuserUneProposition = useCallback(
    (proposition: LigneDeProfil) => {
      void changer((l) => avecLaPropositionRetiree(l, proposition.id));
      api.retirerUneLigneDeProfil(proposition.id).then(
        () => setAnnonce(null),
        (erreur: unknown) => {
          void client.invalidateQueries({ queryKey: cle });
          dire(erreur);
        }
      );
    },
    [changer, client, cle, dire]
  );

  /**
   * « Plus tard » (ticket 03) : le formulaire est noté vu, il se ferme et ne s'ouvre plus seul.
   * Refusé par le serveur, il reste fermé pour cette visite et reviendra à la prochaine.
   */
  const noterLeFormulaireVu = useCallback(() => {
    formulaireFermeCetteVisite = true;
    void changer((l) => ({ ...l, formulaireAMontrer: false }));
    api.noterLeFormulaireVu().catch(() => undefined);
  }, [changer]);

  /**
   * « Enregistrer » (ticket 03) : le profil entier part s'il a changé, puis le formulaire est noté
   * vu. Refusé, rien n'est noté, le refus est dit et le formulaire reste ouvert, ce qui est écrit
   * dedans gardé : la réponse dit s'il peut se fermer.
   */
  const enregistrerLeFormulaire = useCallback(
    async (cases: Record<ChampDuProfil, string>): Promise<boolean> => {
      const avant = lu();
      if (!avant) return false;
      const nouveau = profilDuFormulaire(avant.profil, cases);
      if (nouveau) {
        try {
          const { profil: rendu } = await api.ecrireLeProfil(nouveau);
          await changer((l) => ({ ...l, profil: rendu }));
        } catch (erreur) {
          dire(erreur);
          return false;
        }
      }
      setAnnonce(null);
      noterLeFormulaireVu();
      return true;
    },
    [lu, changer, dire, noterLeFormulaireVu]
  );

  return {
    lecture: lecture.data ?? null,
    charge: lecture.isPending,
    corrigerUnChamp,
    ajouterUneLigne,
    corrigerUneLigne,
    retirerUneLigne,
    garderUneProposition,
    refuserUneProposition,
    noterLeFormulaireVu,
    enregistrerLeFormulaire,
    annonce,
  };
}
